<template>
  <wrapper>
    <section class="challenge-partner-page" v-if="partner">
      <modal :title="`Delete ${partner.name}`" v-if="modal.isVisible('deletePartner')" @close="modal.hide('deletePartner')" :show-footer="false">
        <p class="text-danger lead">This partner will be permanently deleted.</p>
        <p v-if="hasLinks" class="text-warning">
          <i class="fa fa-warning"></i> This partner has links and cannot be deleted. Remove all links first.
        </p>
        <div class="modal-footer">
          <button type="button" class="btn btn-warning" @click="modal.hide('deletePartner')">Cancel</button>
          <loading-button type="button" class="btn btn-danger" @click="deletePartner" :disabled="hasLinks" :isLoading="isDeleting">Delete Permanently</loading-button>
        </div>
      </modal>

      <div class="row">
        <div class="col-md-7">
          <div class="row" v-if="partner.link_counts" style="margin-bottom:20px;">
            <div class="col-md-4">
              <flip-card>
                <dash-box slot="front" title="Schools Linked" :value="partner.link_counts.organisations" :active_value="partner.link_counts.organisations_referred" active_icon="\F0C1" />
                <div slot="back" class="dashbox__help"><strong>{{ partner.link_counts.organisations }}</strong> schools are linked with this partner, <i class="fa fa-link"></i> <strong>{{ partner.link_counts.organisations_referred }}</strong> of those were referred.</div>
              </flip-card>
            </div>
            <div class="col-md-4">
              <flip-card>
                <dash-box slot="front" title="Parents Linked" :value="partner.link_counts.users" :active_value="partner.link_counts.users_referred" active_icon="\F0C1" />
                <div slot="back" class="dashbox__help"><strong>{{ partner.link_counts.users }}</strong> parents are linked with this partner, <i class="fa fa-link"></i> <strong>{{ partner.link_counts.users_referred }}</strong> of those were referred.</div>
              </flip-card>
            </div>
          </div>
        </div>
        <div class="col-md-5">
          <div class="flex justify-content-end column-gap-3 mb-4">
            <button v-if="!isEditing" class="btn btn-primary" @click="startEditing">Edit</button>
            <button v-if="isEditing" class="btn btn-default" @click="isEditing = false">Cancel</button>
            <button class="btn btn-danger" @click="modal.show('deletePartner')" :disabled="hasLinks" :title="hasLinks ? 'Cannot delete a partner that has links' : ''">Delete</button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-7">
          <div class="box box-info">
            <div class="box-body">

              <!-- Read-only view -->
              <table class="table table-striped" v-if="!isEditing">
                <tbody>
                  <tr><th>Slug</th><td>{{ partner.slug }}</td></tr>
                  <tr><th>Category</th><td>{{ partner.category }}</td></tr>
                  <tr><th>Links To</th><td>{{ linksToDisplay }}</td></tr>
                  <tr><th>Contact Email</th><td>{{ partner.contact_email || '—' }}</td></tr>
                  <tr>
                    <th>Website</th>
                    <td>
                      <a v-if="partner.website" :href="partner.website" target="_blank" rel="noopener">{{ partner.website }}</a>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr>
                    <th>Logo</th>
                    <td>
                      <div v-if="partner.logo_url">
                        <img :src="partner.logo_url" :alt="`${partner.name} logo`" class="partner-logo">
                        <div class="text-muted">{{ partner.logo_filename }}</div>
                      </div>
                      <span v-else>—</span>
                    </td>
                  </tr>
                  <tr v-if="partner.referral_url">
                    <th>Referral URL</th>
                    <td>
                      <a :href="partner.referral_url" target="_blank" rel="noopener">{{ partner.referral_url }}</a>
                      <div class="referral-qrcode">
                        <qrcode :value="partner.referral_url" :options="{ width: 160, margin: 1 }" />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Edit form -->
              <challenge-partner-form
                v-if="isEditing"
                :value="editForm"
                :is-loading="isSaving"
                submit-label="Save Changes"
                @validated="handleUpdate"
              />

            </div>
          </div>
        </div>

        <div class="col-md-5">
          <!-- Branches list (if parent) -->
          <div class="box box-info" v-if="!partner.parent_id">
            <div class="box-header with-border">
              <h3 class="box-title">Branches</h3>
              <router-link :to="`/challenges/${challengeSlug}/partners/new?parent=${partner.slug}`" class="btn btn-xs btn-link pull-right">
                <i class="fa fa-plus" style="font-size:0.9em;margin-right:3px;"></i>Create Branch
              </router-link>
            </div>
            <div class="box-body" v-if="sortedChildren.length > 0">
              <ul class="list-unstyled">
                <li v-for="branch in sortedChildren" :key="branch.id" class="mb-2">
                  <router-link :to="`/challenges/${challengeSlug}/partners/${branch.slug}`">
                    <i class="fa fa-circle-o"></i> {{ branch.name }}
                  </router-link>
                </li>
              </ul>
            </div>
            <div class="box-body" v-else>
              <p class="text-muted">No branches yet</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </wrapper>
</template>

<script>
import Wrapper from '@/components/Wrapper';
import DashBox from '@/components/DashBox';
import FlipCard from '@/components/FlipCard';
import ChallengePartnerForm from '@/components/forms/ChallengePartnerForm';
import LoadingButton from '@/components/utils/LoadingButton';
import ModalJS from '@/components/utils/Modal';
import Modal from '@/classes/Modal';

export default {
  name: 'ChallengePartner',
  components: {
    Wrapper,
    DashBox,
    FlipCard,
    ChallengePartnerForm,
    LoadingButton,
    ModalJS, // eslint-disable-line
    modal: ModalJS,
  },
  data() {
    return {
      partner: null,
      isEditing: false,
      isSaving: false,
      isDeleting: false,
      editForm: null,
      modal: new Modal({
        deletePartner: false,
      }),
    };
  },
  computed: {
    challengeSlug() {
      return this.$route.params.challengeSlug;
    },
    partnerSlug() {
      return this.$route.params.partnerSlug;
    },
    hasLinks() {
      if (!this.partner || !this.partner.link_counts) return false;
      const c = this.partner.link_counts;
      return (c.organisations + c.users) > 0;
    },
    linksToDisplay() {
      const labels = { user: 'Parent', reader: 'Child', organisation: 'School' };
      return (this.partner.links_to || []).map((v) => labels[v] || v).join(', ');
    },
    sortedChildren() {
      if (!this.partner || !this.partner.children) return [];
      return [...this.partner.children].sort((a, b) => a.name.localeCompare(b.name));
    },
  },
  mounted() {
    this.fetchPartner();
  },
  methods: {
    fetchPartner() {
      this.$store.dispatch('app/setGlobalLoading', true);
      this.$store.dispatch('challenges/getChallengePartner', {
        challengeSlug: this.challengeSlug,
        partnerSlug: this.partnerSlug,
      }).then((partner) => {
        this.partner = partner;
      }).catch((err) => {
        this.$apiResponse.renderErrorMessage(err);
      }).finally(() => {
        this.$store.dispatch('app/setGlobalLoading', false);
      });
    },
    startEditing() {
      this.editForm = {
        name: this.partner.name,
        slug: this.partner.slug,
        category: this.partner.category,
        links_to: [...this.partner.links_to],
        parent_id: this.partner.parent_id,
        contact_email: this.partner.contact_email || '',
        logo_filename: this.partner.logo_filename || '',
        website: this.partner.website || '',
        logoFile: null,
        removeLogo: false,
      };
      this.isEditing = true;
    },
    handleUpdate(formData) {
      this.isSaving = true;
      const data = new FormData();
      data.append('name', formData.name);
      data.append('slug', formData.slug);
      data.append('category', formData.category);
      (formData.links_to || []).forEach((value) => data.append('links_to[]', value));
      if (formData.parent_id !== null) data.append('parent_id', formData.parent_id);
      if (formData.contact_email) data.append('contact_email', formData.contact_email);
      if (formData.logo_filename) data.append('logo_filename', formData.logo_filename);
      if (formData.website) data.append('website', formData.website);
      if (formData.logoFile) data.append('logo', formData.logoFile);
      if (formData.removeLogo) data.append('remove_logo', '1');

      const payload = {
        challengeSlug: this.challengeSlug,
        partnerSlug: this.partnerSlug,
        data,
      };

      this.$store.dispatch('challenges/updateChallengePartner', payload).then((partner) => {
        this.partner = partner;
        this.isEditing = false;
        if (partner.slug !== this.partnerSlug) {
          this.$router.replace(`/challenges/${this.challengeSlug}/partners/${partner.slug}`);
        }
      }).catch((err) => {
        this.$apiResponse.renderErrorMessage(err);
      }).finally(() => {
        this.isSaving = false;
      });
    },
    deletePartner() {
      this.isDeleting = true;
      this.$store.dispatch('challenges/deleteChallengePartner', {
        challengeSlug: this.challengeSlug,
        partnerSlug: this.partnerSlug,
      }).then(() => {
        this.$router.replace(`/challenges/${this.challengeSlug}`);
      }).catch((err) => {
        this.$apiResponse.renderErrorMessage(err);
      }).finally(() => {
        this.isDeleting = false;
      });
    },
  },
  watch: {
    '$route.params.partnerSlug': function watchPartner() {
      this.isEditing = false;
      this.fetchPartner();
    },
    partner() {
      if (this.partner) {
        const title = this.partner.parent
          ? `${this.partner.parent.name}: ${this.partner.name}`
          : this.partner.name;
        this.$route.meta.title = title;
        this.$route.meta.description = this.partner.slug;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.referral-qrcode {
  margin-top: 10px;
}

.partner-logo {
  max-width: 160px;
  max-height: 160px;
  border: 1px solid #d2d6de;
  padding: 4px;
  background: #fff;
}
</style>
